import { useMutation, useQuery } from '@apollo/client'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import {
  Avatar,
  Button,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  AdAutomationState,
  Automation,
  AutomationState,
  Brand,
  FacebookPage,
  Mutation,
  Query,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useMemo, useState } from 'react'

import LinkChild from '@src/lib/LinkChild'
import { useSimpleConfirmDialog } from '@src/lib/SimpleConfirmDialog'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

import ContentWithTooltip from '../shared/ContentWithTooltip'

import {
  CREATE_ORGANIC_AUTOMATION,
  CREATE_PAID_AUTOMATION,
  GET_ALL_AUTOMATIONS,
  UPDATE_ORGANIC_AUTOMATION,
  UPDATE_PAID_AUTOMATION,
} from './RetailerSettingsAutomations'
import retailerSettingsPaths from './retailerSettingsPaths'

const RetailerSettingsBrandSettingsAutomations: React.FC = () => {
  const { confirm } = useSimpleConfirmDialog()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const params = retailerSettingsPaths.brandSettingsAutomations.useParams()
  const retailer = ensureCurrentRetailerAccount?.retailers?.find(
    (x) => String(x.brand?.slug) === String(params.brandSlug),
  )
  // const brand = retailer?.brand
  const [inProgress, setInProgress] = useState<string[]>([])

  const { data, loading, refetch } = useQuery<Query>(GET_ALL_AUTOMATIONS, {
    variables: {
      id: ensureCurrentRetailerAccount.id,
      retailerIds:
        ensureCurrentRetailerAccount.retailers?.map((x) => x.id) || [],
    },
  })

  const [createOrganicAutomation] = useMutation<Mutation>(
    CREATE_ORGANIC_AUTOMATION,
  )
  const [updateOrganicAutomation] = useMutation<Mutation>(
    UPDATE_ORGANIC_AUTOMATION,
  )
  const [createPaidAutomation] = useMutation<Mutation>(CREATE_PAID_AUTOMATION)
  const [updatePaidAutomation] = useMutation<Mutation>(UPDATE_PAID_AUTOMATION)

  const { organicAutomations } = useMemo(() => {
    const organicAutomations: OrganicAutomationsLookup = {}

    for (const automation of data?.getAutomations || []) {
      organicAutomations[getOrganicAutomationCacheKey(automation)] = automation
    }

    return {
      organicAutomations,
    }
  }, [data?.getAutomations])

  function findPaidAdAutomation(
    brand_id: Brand['id'],
    facebook_page_id: FacebookPage['id'],
  ) {
    return (
      data?.getAdAutomationsByRetailerIds.filter(
        (automation) =>
          automation.brand_id === brand_id &&
          automation.customization?.facebook_page_id === facebook_page_id,
      ) || []
    )
  }

  async function temporarilyDisableSwitch(id: string, cb: () => Promise<void>) {
    setInProgress((prev) => {
      if (prev.includes(id)) {
        return prev
      }

      return [...prev, id]
    })

    await cb()

    setInProgress((prev) => {
      return prev.filter((x) => x !== id)
    })
  }

  // Arbitrary, based off the CSS height of a Switch.
  const SKELETON_HEIGHT = 38

  return (
    <Stack direction="column" spacing={4} sx={{ marginTop: 5 }}>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {I18n.t(
                    'retailerSettings.socialAccounts.socialAccountsTable.headers.name',
                  )}
                </TableCell>
                <TableCell width="50" align="center">
                  {I18n.t('retailerSettings.automations.headers.facebook')}
                </TableCell>
                <TableCell width="50" align="center">
                  {I18n.t('retailerSettings.automations.headers.instagram')}
                </TableCell>
                <TableCell width="50" align="center">
                  <ContentWithTooltip
                    title={I18n.t('retailerSettings.automations.tooltips.paid')}
                  >
                    {I18n.t('retailerSettings.automations.headers.paid')}
                  </ContentWithTooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ensureCurrentRetailerAccount.facebook_pages?.map(
                (facebook_page) => {
                  const baseCacheKey = `${facebook_page.__typename}-${facebook_page.id}`
                  const doesPageHaveInstagram = Boolean(
                    facebook_page.instagram_business_account_id &&
                      facebook_page.remote_token_status?.is_valid,
                  )

                  const organicFacebookCacheKey = `${baseCacheKey}-${retailer?.id}-facebook`
                  const organicInstagramCacheKey = `${baseCacheKey}-${retailer?.id}-instagram`
                  const paidInProgressKey = `${baseCacheKey}-${retailer?.id}`
                  const paidAdAutomations = findPaidAdAutomation(
                    retailer?.brand?.id || '',
                    facebook_page.id,
                  )
                  const isPaidAutomated = paidAdAutomations.some(
                    (automation) => automation.state === 'enabled',
                  )
                  const isBrandAutomatedToDifferentPage = Boolean(
                    (
                      data?.getAdAutomationsByRetailerIds.filter(
                        (automation) =>
                          automation.customization?.facebook_page_id !==
                            facebook_page.id &&
                          automation.state === 'enabled' &&
                          automation.brand_id === retailer?.brand?.id,
                      ) || []
                    ).length > 0,
                  )

                  const instagramChecked = Boolean(
                    organicAutomations[organicInstagramCacheKey]?.status ===
                      'active',
                  )

                  return (
                    <TableRow>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar
                            alt={facebook_page.name || ''}
                            src={facebook_page.picture_url || undefined}
                          />
                          <FacebookIcon color="facebook" />
                          <InstagramIcon
                            color={
                              doesPageHaveInstagram ? 'instagram' : 'disabled'
                            }
                          />
                          <span>{facebook_page.name}</span>
                        </Stack>
                      </TableCell>

                      <TableCell align="center">
                        {loading ? (
                          <Skeleton height={SKELETON_HEIGHT} />
                        ) : (
                          <Switch
                            disabled={inProgress.includes(
                              organicFacebookCacheKey,
                            )}
                            checked={Boolean(
                              organicAutomations[organicFacebookCacheKey]
                                ?.status === 'active',
                            )}
                            onChange={(_event, checked) => {
                              temporarilyDisableSwitch(
                                organicFacebookCacheKey,
                                async () => {
                                  const existing =
                                    organicAutomations[organicFacebookCacheKey]

                                  if (existing) {
                                    const state: AutomationState = checked
                                      ? 'enabled'
                                      : 'disabled'
                                    if (
                                      !checked &&
                                      !(await confirm({
                                        title: I18n.t(
                                          'retailerSettings.automations.confirmationDialog.title',
                                        ),
                                        content: I18n.t(
                                          'retailerSettings.automations.confirmationDialog.content',
                                        ),
                                      }))
                                    ) {
                                      // user has cancelled the action
                                      return
                                    }
                                    await updateOrganicAutomation({
                                      variables: {
                                        automationId: existing.id,
                                        state,
                                      },
                                    })
                                  } else {
                                    await createOrganicAutomation({
                                      variables: {
                                        retailerId: retailer?.id,
                                        automationableType:
                                          facebook_page.__typename,
                                        automationableId: facebook_page.id,
                                        category: 'facebook',
                                      },
                                    })
                                  }

                                  await refetch()
                                },
                              )
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {loading ? (
                          <Skeleton height={SKELETON_HEIGHT} />
                        ) : (
                          <Tooltip
                            title={
                              doesPageHaveInstagram
                                ? undefined
                                : I18n.t(
                                    'retailerSettings.automations.tooltips.noInstagram',
                                  )
                            }
                          >
                            <div>
                              <Switch
                                disabled={
                                  (instagramChecked
                                    ? false
                                    : !doesPageHaveInstagram) ||
                                  inProgress.includes(organicInstagramCacheKey)
                                }
                                checked={instagramChecked}
                                onChange={(_event, checked) => {
                                  temporarilyDisableSwitch(
                                    organicInstagramCacheKey,
                                    async () => {
                                      const existing =
                                        organicAutomations[
                                          organicInstagramCacheKey
                                        ]

                                      if (existing) {
                                        const state: AutomationState = checked
                                          ? 'enabled'
                                          : 'disabled'
                                        // ask user to confirm if they are disabling
                                        if (
                                          !checked &&
                                          !(await confirm({
                                            title: I18n.t(
                                              'retailerSettings.automations.confirmationDialog.title',
                                            ),
                                            content: I18n.t(
                                              'retailerSettings.automations.confirmationDialog.content',
                                            ),
                                          }))
                                        ) {
                                          // user has cancelled the action
                                          return
                                        }
                                        await updateOrganicAutomation({
                                          variables: {
                                            automationId: existing.id,
                                            state,
                                          },
                                        })
                                      } else {
                                        await createOrganicAutomation({
                                          variables: {
                                            retailerId: retailer?.id,
                                            automationableType:
                                              facebook_page.__typename,
                                            automationableId: facebook_page.id,
                                            category: 'instagram',
                                          },
                                        })
                                      }

                                      await refetch()
                                    },
                                  )
                                }}
                              />
                            </div>
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {loading ? (
                          <Skeleton height={SKELETON_HEIGHT} />
                        ) : (
                          <Tooltip
                            title={
                              isBrandAutomatedToDifferentPage
                                ? I18n.t(
                                    'retailerSettings.automations.tooltips.paidOnAnotherBrand',
                                  )
                                : undefined
                            }
                          >
                            <div>
                              <Switch
                                checked={isPaidAutomated}
                                disabled={
                                  Boolean(isBrandAutomatedToDifferentPage) ||
                                  inProgress.includes(paidInProgressKey)
                                }
                                onChange={async (_event, checked) => {
                                  temporarilyDisableSwitch(
                                    paidInProgressKey,
                                    async () => {
                                      const existing =
                                        data?.getAdAutomationsByRetailerIds.find(
                                          (x) =>
                                            x.brand_id === retailer?.brand?.id,
                                        )
                                      // const locations: AdAutomationCustomLocationsInput[] =
                                      //   []
                                      const state: AdAutomationState = checked
                                        ? 'enabled'
                                        : 'disabled'

                                      if (existing) {
                                        // ask user to confirm if they are disabling
                                        if (
                                          !checked &&
                                          !(await confirm({
                                            title: I18n.t(
                                              'retailerSettings.automations.confirmationDialog.title',
                                            ),
                                            content: I18n.t(
                                              'retailerSettings.automations.confirmationDialog.content',
                                            ),
                                          }))
                                        ) {
                                          // user has cancelled the action
                                          return
                                        }
                                        await updatePaidAutomation({
                                          variables: {
                                            automationId: existing.id,
                                            retailerId: retailer?.id,
                                            channel: 'facebook',
                                            facebook_page_id: facebook_page.id,
                                            state,
                                            // custom_locations: locations,
                                          },
                                        })
                                      } else {
                                        await createPaidAutomation({
                                          variables: {
                                            brand_id: retailer?.brand?.id,
                                            retailer_id: retailer?.id,
                                            channel: 'facebook',
                                            facebook_page_id: facebook_page.id,
                                            state,
                                            // custom_locations: locations,
                                          },
                                        })
                                      }

                                      await refetch()
                                    },
                                  )
                                }}
                              />
                            </div>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                },
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Divider />

      <Typography variant="body2" color="text.secondary" textAlign="center">
        <ContentWithTooltip
          slotProps={{
            stack: { justifyContent: 'center' },
          }}
          title={I18n.t(
            'retailerSettings.automations.tooltips.automationUsage',
          )}
        >
          {I18n.t('retailerSettings.automations.socialAccounts.message')}
        </ContentWithTooltip>

        <br />
        <br />
        <LinkChild
          to={retailerSettingsPaths.socialAccounts.build({
            retailerAccountSlug: ensureCurrentRetailerAccount.slug || '',
          })}
        >
          <Button variant="outlined" size="small">
            {I18n.t('retailerSettings.automations.socialAccounts.buttonLabel')}
          </Button>
        </LinkChild>
      </Typography>
    </Stack>
  )
}

type OrganicAutomationsLookup = Record<string, Automation>

function getOrganicAutomationCacheKey(automation: Automation) {
  return `${automation.automationable_type}-${automation.automationable_id}-${automation.retailer_id}-${automation.category}`
}

export default RetailerSettingsBrandSettingsAutomations
